import React, { useState } from 'react'
import { Modal } from '../UI/Modal/Modal'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SamplesApi } from '../../services/samplesApi'
import { SampleRequestPreview } from './SampleRequestPreview'
import { Spinner } from 'react-bootstrap'
import { CancelSampleRequestModal } from './CancelSampleRequestModal'
import { toast } from 'react-toastify'
import to from 'await-to-js'

interface SampleRequestPreviewModalProps {
  show: boolean
  onHide: () => void
  sampleRequestId: number | null
}

export function SampleRequestPreviewModal({
  show,
  onHide,
  sampleRequestId,
}: SampleRequestPreviewModalProps) {
  const queryClient = useQueryClient()
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const { data: order, isLoading } = useQuery({
    queryKey: ['sample-request', sampleRequestId],
    queryFn: () => SamplesApi.getSampleRequest(sampleRequestId!),
    enabled: !!sampleRequestId && show,
  })

  const handleCancelClick = () => {
    setCancelModalOpen(true)
  }

  const handleCancelConfirm = async (reason: string) => {
    if (!sampleRequestId) return

    setIsCancelling(true)
    const [err] = await to(
      SamplesApi.cancelSampleRequest(sampleRequestId, reason)
    )

    setIsCancelling(false)

    if (!err) {
      toast.success('Sample request cancelled successfully')
      setCancelModalOpen(false)
      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({
        queryKey: ['sample-request', sampleRequestId],
      })
      await queryClient.invalidateQueries({ queryKey: ['sample-requests'] })
    } else {
      toast.error('Failed to cancel sample request')
    }
  }

  const canCancel = order?.status === 'submitted'

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(open) => !open && onHide()}
        title="Sample Request Details"
        description="View sample request information"
        size="lg"
        hideCancelButton={!canCancel}
        onAlt={canCancel ? handleCancelClick : undefined}
        altButtonText="Cancel Request"
        altButtonProps={{ variant: 'danger' }}
      >
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : order ? (
          <SampleRequestPreview order={order} showHeader={true} />
        ) : (
          <div className="text-center py-5 text-muted">
            Sample order not found
          </div>
        )}
      </Modal>

      <CancelSampleRequestModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        onConfirm={handleCancelConfirm}
        isSubmitting={isCancelling}
        requestNumber={order?.request_number}
      />
    </>
  )
}
